import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import * as L from 'leaflet';
import 'leaflet.heat';
import { DashboardService, IPositioningModel } from '../../services/dashboard.service';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { DashboardAverageParkingLoadModel } from '../../models';
import { Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessagesService } from '@shared/modules/messages';

@Component({
  selector: 'aggregator-dashboard-city-map-chart',
  templateUrl: './city-map-chart.component.html',
  styleUrl: './city-map-chart.component.scss',
  standalone: true,
  imports: [NzProgressModule],
})
export class DashboardCityMapChartComponent implements OnInit, OnDestroy {
  map!: L.Map;
  data?: DashboardAverageParkingLoadModel;
  @Input() filter!: { startDate: string; endDate: string };

  private markersLayer!: L.LayerGroup;
  private parkingData: IPositioningModel[] = [];
  private readonly MARKER_ZOOM_THRESHOLD = 18;

  constructor(
    private dashboardService: DashboardService,
    private destroyRef: DestroyRef,
    private messagesService: MessagesService,
  ) {}

  ngOnInit(): void {
    this.initMap();
    this.loadParkingHeatmap();
  }

  get filterValue(): { startDate: string; endDate: string } {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  }

  initMap(): void {
    this.map = L.map('leaflet-map').setView([51.1, 71.55], 10);

    const API_KEY = '9a146954-2a29-47b1-ab6a-17ffee0e02d2';
    L.tileLayer(
      'https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png?api_key=' + API_KEY,
      {
        minZoom: 9,
        maxZoom: 20,
        attribution: '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>',
        detectRetina: true,
      },
    ).addTo(this.map);

    // Initialize markers layer
    this.markersLayer = L.layerGroup();

    // Add zoom event handler
    this.map.on('zoomend', () => {
      this.updateMarkersVisibility();
    });
  }

  occupiedSpotsPercent = 0;

  loadParkingHeatmap(): void {
    this.dashboardService
      .getPositioning()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
        if (!response.isSuccess) {
          this.messagesService.showError('Непредвиденная ошибка');
          return;
        }
        const data = response?.value?.items ?? [];

        // Store parking data for markers
        this.parkingData = data;

        const totalSpots = data.reduce((acc, p) => acc + (p.totalSpots || 0), 0);
        const freeSpots = data.reduce((acc, p) => acc + (p.freeSpotsCount || 0), 0);
        const occupiedSpots = totalSpots - freeSpots;

        this.occupiedSpotsPercent =
          totalSpots === 0 ? 0 : Math.round((occupiedSpots / totalSpots) * 100);

        const heatData: [number, number, number][] = data
          .filter(p => p.location.x && p.location.y)
          .map(p => {
            const lat = p.location.y;
            const lng = p.location.x;

            const total = p.totalSpots || 1;
            const free = p.freeSpotsCount || 0;

            const loadRatio = (total - free) / total;

            return [lat, lng, loadRatio];
          });

        L.heatLayer(heatData, {
          radius: 30,
          blur: 25,
          maxZoom: 20,
          minOpacity: 0.4,
          gradient: {
            0.0: '#49d638',
            0.52: '#ffce46',
            1.0: '#ff0000',
          },
        }).addTo(this.map);

        // Update markers visibility based on current zoom
        this.updateMarkersVisibility();
      });
  }

  private updateMarkersVisibility(): void {
    const currentZoom = this.map.getZoom();

    if (currentZoom >= this.MARKER_ZOOM_THRESHOLD) {
      // Add markers if not already added
      if (!this.map.hasLayer(this.markersLayer)) {
        // Clear existing markers
        this.markersLayer.clearLayers();

        // Create markers for each parking location
        this.parkingData.forEach(parking => {
          const marker = this.createMarker(parking);
          if (marker) {
            this.markersLayer.addLayer(marker);
          }
        });

        // Add markers layer to map
        this.markersLayer.addTo(this.map);
      }
    } else {
      // Remove markers if zoom is below threshold
      if (this.map.hasLayer(this.markersLayer)) {
        this.map.removeLayer(this.markersLayer);
      }
    }
  }

  private createMarker(parking: IPositioningModel): L.Marker | null {
    const lat = parking.location.y;
    const lng = parking.location.x;

    if (!lat || !lng) {
      return null;
    }

    // Calculate load ratio
    const total = parking.totalSpots || 1;
    const free = parking.freeSpotsCount || 0;
    const loadRatio = (total - free) / total;

    // Get marker color based on load ratio
    const color = this.getMarkerColor(loadRatio);

    // Create marker icon
    const markerIcon = L.divIcon({
      className: 'parking-marker',
      html: `<div style="background-color: ${color}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 4px rgba(0,0,0,0.3);"></div>`,
      iconSize: [12, 12],
      iconAnchor: [6, 6],
    });

    // Create and return marker
    return L.marker([lat, lng], { icon: markerIcon });
  }

  private getMarkerColor(loadRatio: number): string {
    // Gradient: 0.0 -> green, 0.52 -> yellow, 1.0 -> red
    // Same as heatmap gradient
    if (loadRatio <= 0.52) {
      // Interpolate between green (#49d638) and yellow (#ffce46)
      const t = loadRatio / 0.52;
      return this.interpolateColor('#49d638', '#ffce46', t);
    } else {
      // Interpolate between yellow (#ffce46) and red (#ff0000)
      const t = (loadRatio - 0.52) / (1.0 - 0.52);
      return this.interpolateColor('#ffce46', '#ff0000', t);
    }
  }

  private interpolateColor(color1: string, color2: string, factor: number): string {
    const c1 = parseInt(color1.slice(1), 16);
    const c2 = parseInt(color2.slice(1), 16);

    const r1 = (c1 >> 16) & 0xff;
    const g1 = (c1 >> 8) & 0xff;
    const b1 = c1 & 0xff;

    const r2 = (c2 >> 16) & 0xff;
    const g2 = (c2 >> 8) & 0xff;
    const b2 = c2 & 0xff;

    const r = Math.round(r1 + (r2 - r1) * factor);
    const g = Math.round(g1 + (g2 - g1) * factor);
    const b = Math.round(b1 + (b2 - b1) * factor);

    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.off();
      if (this.markersLayer) {
        this.markersLayer.clearLayers();
      }
      this.map.remove();
    }
  }
}
